import useRequest from "@/lib/request/useRequest";
import { Organization } from "@/types/organization";
import { PaginationResponse } from "@/types/pagination";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
    faCheck,
    faLeaf,
    faXmark,
    faCheckCircle,
    faFileZipper,
    faClipboard,
    faDrawPolygon,
    faCircleExclamation,
    faArrowUpRightFromSquare,
    faCheckDouble,
    faBars,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Chip,
    Divider,
    ModalBody,
    ModalHeader,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Button,
    Switch,
    addToast,
} from "@heroui/react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { User } from "@/types/user";
import { useInfiniteQuery } from "@tanstack/react-query";
import BulkPlantingOperationForm from "@/components/home/BulkPlantingOperationForm";
import BulkRxExport from "@/components/home/BulkRxExport";
import BulkLogRxApplied from "@/components/home/BulkLogRxApplied";
import { FieldConfig } from "@/types/fieldConfig";
import BulkPlotRxForm from "@/components/home/BulkPlotRxForm";
import InfoModal from "@/components/common/InfoModal";
import BulkConfirmPlots from "@/components/home/BulkConfirmPlots";
import { FieldFilterBar } from "@/components/fields/FieldFilterBar";
import {
    createFieldColumns,
    FieldColumnOptions,
} from "@/components/fields/fieldColumns";
import TableV2 from "@/components/table/TableV2";

type FieldConfigSearch = {
    search?: string;
    ordering?: string;
    service_provider?: string;
    grower?: string;
    account_manager?: string;
    created_date?: string;
};

export const Route = createFileRoute("/dashboard/home/field-config")({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>): FieldConfigSearch => {
        return {
            search: search.search as string,
            ordering: search.ordering as string,
            service_provider: search.service_provider as string,
            grower: search.grower as string,
            account_manager: search.account_manager as string,
            created_date: search.created_date as string,
        };
    },
});

function RouteComponent() {
    const { useGetRequest, axiosRequest } = useRequest();

    const {
        search,
        ordering,
        service_provider,
        grower,
        account_manager,
        created_date,
    } = Route.useSearch();

    const navigate = Route.useNavigate();

    const serviceProvidersQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
    });

    const growersQuery = useGetRequest<Organization[]>({
        url: "organizations/farms/",
    });

    const accountManagersQuery = useGetRequest<User[]>({
        url: "users/account_managers/",
    });

    const growersById = useMemo(() => {
        return (
            growersQuery.data?.reduce(
                (acc, grower) => {
                    acc[grower.id] = grower;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [growersQuery.data]);

    const serviceProvidersById = useMemo(() => {
        return (
            serviceProvidersQuery.data?.reduce(
                (acc, serviceProvider) => {
                    acc[serviceProvider.id] = serviceProvider;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [serviceProvidersQuery.data]);

    const accountManagersById = useMemo(() => {
        return (
            accountManagersQuery.data?.reduce(
                (acc, accountManager) => {
                    acc[accountManager.id] = accountManager;
                    return acc;
                },
                {} as Record<number, User>,
            ) ?? {}
        );
    }, [accountManagersQuery.data]);

    const fieldsQuery = useInfiniteQuery<PaginationResponse<FieldConfig>>({
        queryKey: [
            "fields",
            "config",
            search,
            ordering,
            service_provider,
            grower,
            account_manager,
            created_date,
        ],
        getNextPageParam: (lastPage) => {
            return lastPage.next ? lastPage.page + 1 : undefined;
        },
        getPreviousPageParam: (firstPage) => {
            return firstPage.previous ? firstPage.page - 1 : undefined;
        },
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            return await axiosRequest<PaginationResponse<FieldConfig>>({
                url: "fields/config/",
                params: {
                    search,
                    ordering: ordering === "" ? "name" : ordering,
                    service_provider,
                    grower,
                    account_manager,
                    created_date,
                    page: pageParam,
                },
            });
        },
    });

    const tableData = useMemo(() => {
        return fieldsQuery.data?.pages.flatMap((page) => page.results) ?? [];
    }, [fieldsQuery.data?.pages]);

    const columns: ColumnDef<FieldConfig>[] = useMemo(() => {
        const columnOptions: FieldColumnOptions = {
            growersById,
            serviceProvidersById,
            accountManagersById,
        };

        return [
            createFieldColumns.selection<FieldConfig>(),
            createFieldColumns.growerAndServiceProvider<FieldConfig>(
                columnOptions,
            ),
            createFieldColumns.fieldName<FieldConfig>(),
            {
                id: "activeCrop",
                header: () => {
                    return (
                        <div className="flex flex-row items-center gap-2 ">
                            Active Crop
                            <InfoModal>
                                <ModalHeader className="font-semibold text-xl">
                                    Active Crop
                                </ModalHeader>
                                <ModalBody>
                                    <ul className="list-disc ml-6 space-y-2">
                                        <li>
                                            The active crop & planting date are
                                            used to maximize the accuracy of
                                            Sentinel insights
                                        </li>
                                        <li>
                                            The planting date does not need to
                                            be exact if the info isn't
                                            available. It can always be updated
                                            later.
                                        </li>
                                        <li>
                                            The default crop and planting date
                                            is Corn planted on May 1st.
                                        </li>
                                    </ul>
                                </ModalBody>
                            </InfoModal>
                        </div>
                    );
                },
                enableSorting: false,
                accessorFn: (row) =>
                    row?.active_planting_operation?.crop_configuration
                        ?.crop_name ?? "",
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-col items-start w-full text-sm pr-2">
                            <div className="font-bold">
                                {
                                    row.original.active_planting_operation
                                        ?.crop_configuration?.crop_name
                                }
                            </div>
                            <div className="flex flex-row items-center justify-between w-full">
                                <span className="text-xs text-gray-500">
                                    {dayjs(
                                        row.original.active_planting_operation
                                            ?.start_date,
                                    ).format("ll")}
                                </span>
                                {row.original.active_planting_operation?.id !==
                                -1 ? (
                                    <Chip
                                        color="secondary"
                                        size="sm"
                                        classNames={{
                                            base: "text-xs",
                                        }}
                                    >
                                        Logged
                                    </Chip>
                                ) : (
                                    <Chip
                                        color="default"
                                        size="sm"
                                        classNames={{
                                            base: "text-xs",
                                        }}
                                    >
                                        Default
                                    </Chip>
                                )}
                            </div>
                        </div>
                    );
                },
                size: 180,
            },
            {
                id: "plotStatus",
                header: () => {
                    return (
                        <div className="flex flex-row items-center gap-2 ">
                            Plot Status
                            <InfoModal>
                                <ModalHeader className="font-semibold text-xl">
                                    Plot Status
                                </ModalHeader>
                                <ModalBody>
                                    <h3 className="font-semibold mb-2">
                                        Sentinel Plots need to be implemented to
                                        create high and low Nitrogen
                                        environments and power Insights.
                                    </h3>
                                    <ul className="list-disc ml-6 space-y-2">
                                        <li>Create a Sentinel Plot Rx</li>
                                        <li>
                                            Download the Rx as a file or export
                                            it directly to your connections
                                        </li>
                                        <li>
                                            Log the application to activate
                                            plots for Insights
                                        </li>
                                        <li>
                                            Confirm the plots locations for
                                            accuracy
                                        </li>
                                    </ul>
                                </ModalBody>
                            </InfoModal>
                        </div>
                    );
                },
                accessorFn: (row) => row?.plot_status ?? "",
                enableSorting: false,
                cell: ({ row }) => {
                    let chipContent = {
                        text: "Create Plot Rx",
                        color: "danger" as
                            | "primary"
                            | "secondary"
                            | "success"
                            | "warning"
                            | "danger",
                        icon: faDrawPolygon,
                        link: "/dashboard/fields/$fieldId/rx/new",
                    };

                    if (row.original.plots_stale) {
                        chipContent = {
                            text: "Stale Plots",
                            color: "danger",
                            icon: faCircleExclamation,
                            link: "/dashboard/fields/$fieldId/rx/new",
                        };
                    } else if (row.original.plots_uploaded) {
                        chipContent = {
                            text: "Plots Uploaded",
                            color: "success",
                            icon: faCheckCircle,
                            link: "/dashboard/fields/$fieldId/insights/settings",
                        };
                    } else if (
                        row.original.latest_plot_prescription_id &&
                        row.original.latest_prescription_has_active_plots &&
                        row.original.latest_prescription_application &&
                        row.original.plots_confirmed
                    ) {
                        chipContent = {
                            text: "Plots Confirmed",
                            color: "success",
                            icon: faCheckCircle,
                            link: "/dashboard/fields/$fieldId/insights/settings",
                        };
                    } else if (
                        row.original.latest_plot_prescription_id &&
                        row.original.latest_prescription_application &&
                        !row.original.plots_confirmed
                    ) {
                        chipContent = {
                            text: "Confirm Plots",
                            color: "warning",
                            icon: faCheckDouble,
                            link: "/dashboard/fields/$fieldId/insights/settings",
                        };
                    } else if (
                        row.original.latest_plot_prescription_id &&
                        row.original.latest_plot_prescription_exported &&
                        !row.original.latest_prescription_application
                    ) {
                        chipContent = {
                            text: "Log Application",
                            color: "danger",
                            icon: faClipboard,
                            link: "/dashboard/fields/$fieldId/operations/add",
                        };
                    } else if (
                        row.original.latest_plot_prescription_id &&
                        !row.original.latest_plot_prescription_exported
                    ) {
                        chipContent = {
                            text: "Export Plot Rx",
                            color: "danger",
                            icon: faFileZipper,
                            link: `/dashboard/fields/$fieldId/rx/${row.original.latest_plot_prescription_id}`,
                        };
                    } else if (!row.original.latest_plot_prescription_id) {
                        chipContent = {
                            text: "Create Plot Rx",
                            color: "danger",
                            icon: faDrawPolygon,
                            link: "/dashboard/fields/$fieldId/rx/new",
                        };
                    }

                    return (
                        <div className="flex flex-col items-start justify-center w-full gap-1">
                            <div className="flex flex-row gap-1 ml-3">
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className={
                                        row.original.latest_plot_prescription_id
                                            ? "text-green-500"
                                            : "text-gray-300"
                                    }
                                    title="Prescription Created"
                                />
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className={
                                        row.original
                                            .latest_plot_prescription_exported
                                            ? "text-green-500"
                                            : "text-gray-300"
                                    }
                                    title="Prescription Exported"
                                />
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className={
                                        row.original
                                            .latest_prescription_application
                                            ? "text-green-500"
                                            : "text-gray-300"
                                    }
                                    title="Application Logged"
                                />
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className={
                                        row.original.plots_confirmed
                                            ? "text-green-500"
                                            : "text-gray-300"
                                    }
                                    title="Plots Corrected"
                                />
                            </div>
                            <Chip
                                as={Link}
                                to={chipContent.link}
                                params={{
                                    // @ts-expect-error ts(2322 )
                                    fieldId: row.original.id.toString(),
                                }}
                                target="_blank"
                                color={chipContent.color}
                                endContent={
                                    <FontAwesomeIcon
                                        icon={chipContent.icon}
                                        className="mr-2"
                                    />
                                }
                                variant="flat"
                                className="text-xs cursor-pointer"
                            >
                                {chipContent.text}
                            </Chip>
                        </div>
                    );
                },
                size: 180,
            },
            createFieldColumns.irrigation<FieldConfig>(),
            {
                id: "managementZone",
                header: () => {
                    return (
                        <div className="flex flex-row items-center gap-2">
                            Zones
                            <InfoModal>
                                <ModalHeader className="font-semibold text-xl">
                                    Zones
                                </ModalHeader>
                                <ModalBody>
                                    <ul>
                                        <li>
                                            This indicates whether the field has
                                            a primary management zone.
                                        </li>
                                    </ul>
                                </ModalBody>
                            </InfoModal>
                        </div>
                    );
                },
                accessorFn: (row) =>
                    row?.has_primary_management_zones ? "Yes" : "No",
                enableSorting: false,
                cell: ({ row }) => {
                    return (
                        <Chip
                            as={Link}
                            to="/dashboard/fields/$fieldId/setup/management-zones"
                            params={{
                                // @ts-expect-error ts(2322 )
                                fieldId: row.original.id.toString(),
                            }}
                            target="_blank"
                            color={
                                row.original.has_primary_management_zones
                                    ? "secondary"
                                    : "default"
                            }
                            size="sm"
                            startContent={
                                <FontAwesomeIcon
                                    icon={
                                        row.original
                                            .has_primary_management_zones
                                            ? faCheck
                                            : faXmark
                                    }
                                    className="ml-2 mr-1"
                                />
                            }
                        >
                            {row.original.has_primary_management_zones
                                ? "Yes"
                                : "No "}
                        </Chip>
                    );
                },
                size: 100,
            },
            {
                id: "connections",
                header: () => {
                    return (
                        <div className="flex flex-row items-center gap-2">
                            Connections
                            <InfoModal>
                                <ModalHeader className="font-semibold text-xl">
                                    Connections
                                </ModalHeader>
                                <ModalBody className="flex flex-col gap-2">
                                    <ul>
                                        <li>
                                            This indicates whether the field is
                                            connected to an external system like
                                            John Deere Operations Center, Case
                                            IH AFS Connect, etc.
                                        </li>
                                        <li></li>
                                    </ul>
                                    <Button
                                        as={Link}
                                        to="/dashboard/account-settings/connections"
                                        target="_blank"
                                        color="primary"
                                        fullWidth
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faArrowUpRightFromSquare}
                                            />
                                        }
                                    >
                                        Go to Connections Page
                                    </Button>
                                </ModalBody>
                            </InfoModal>
                        </div>
                    );
                },
                accessorFn: (row) => row?.connections_list,
                enableSorting: false,
                cell: ({ row }) => {
                    return (
                        <div
                            className={classNames({
                                "text-xs text-gray-500": true,
                                "text-secondary-500":
                                    !!row.original.connections_list &&
                                    row.original.connections_list?.length > 0,
                            })}
                        >
                            {!row.original.connections_list ||
                            row.original.connections_list?.length == 0 ? (
                                "None"
                            ) : (
                                <>
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="mr-1"
                                    />
                                    {row.original.connections_list?.length}{" "}
                                    connection
                                    {row.original.connections_list?.length > 1
                                        ? "s"
                                        : ""}
                                </>
                            )}
                        </div>
                    );
                },
                size: 130,
            },
            {
                id: "drainage",
                header: "Drainage",
                accessorFn: (row) => row.has_drainage,
                cell: ({ getValue, row, table }) => {
                    const initialValue = getValue() as boolean;
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const [isSelected, setIsSelected] = useState(initialValue);

                    const onValueChange = (value: boolean) => {
                        setIsSelected(value);
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (table?.options.meta as any).updateDrainage(
                            row.original.id,
                            value,
                        );
                    };

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        setIsSelected(initialValue);
                    }, [initialValue]);

                    return (
                        <Switch
                            size="sm"
                            isSelected={isSelected}
                            onValueChange={onValueChange}
                        />
                    );
                },
            },
        ];
    }, [growersById, serviceProvidersById, accountManagersById]);

    const [sorting, setSorting] = useState<SortingState>([
        {
            id: "name",
            desc: false,
        },
    ]);

    const [selectedFields, setSelectedFields] = useState<FieldConfig[]>([]);

    useEffect(() => {
        if (sorting.length > 0) {
            navigate({
                search: (prev) => ({
                    ...prev,
                    ordering: `${sorting[0].desc ? "-" : ""}${sorting[0].id}`,
                }),
            });
        } else {
            navigate({
                search: (prev) => ({ ...prev, ordering: "" }),
            });
        }
    }, [sorting, navigate]);

    const isLoading = useMemo(() => {
        return (
            fieldsQuery.isLoading ||
            serviceProvidersQuery.isLoading ||
            growersQuery.isLoading
        );
    }, [
        fieldsQuery.isLoading,
        growersQuery.isLoading,
        serviceProvidersQuery.isLoading,
    ]);

    const growerOptions = useMemo(() => {
        return (
            growersQuery.data?.map((grower) => ({
                label: grower.name,
                key: grower.id.toString(),
            })) ?? []
        );
    }, [growersQuery.data]);

    const serviceProviderOptions = useMemo(() => {
        return (
            serviceProvidersQuery.data?.map((serviceProvider) => ({
                label: serviceProvider.name,
                key: serviceProvider.id.toString(),
            })) ?? []
        );
    }, [serviceProvidersQuery.data]);

    const handleSelectionChange = (selected: FieldConfig[]) => {
        setSelectedFields(selected);
    };

    const [showPlantingForm, setShowPlantingForm] = useState<boolean>(false);

    const [showPlotRxForms, setShowPlotRxForms] = useState<boolean>(false);

    const [showBulkExport, setShowBulkExport] = useState<boolean>(false);

    const [showLogApplicationForm, setShowLogApplicationForm] =
        useState<boolean>(false);

    const [showPlotConfirmation, setShowPlotConfirmation] =
        useState<boolean>(false);

    const meta = useMemo(() => {
        return {
            updateDrainage: (fieldId: number, value: boolean) => {
                axiosRequest({
                    url: `fields/${fieldId}/`,
                    method: "patch",
                    data: {
                        has_drainage: value,
                    },
                }).then(() => {
                    addToast({
                        title: "Drainage updated successfully",
                        color: "success",
                    });
                });
            },
        };
    }, [axiosRequest]);

    return (
        <div className="h-full flex flex-col overflow-hidden">
            <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                <div className="flex flex-row gap-2 items-center">
                    <FieldFilterBar
                        searchValue={search}
                        onSearchChange={(value) => {
                            navigate({
                                search: (prev) => ({ ...prev, search: value }),
                            });
                        }}
                        serviceProviderId={service_provider}
                        onServiceProviderChange={(id) => {
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    service_provider: id ?? "",
                                }),
                            });
                        }}
                        serviceProviderOptions={serviceProviderOptions}
                        growerId={grower}
                        onGrowerChange={(id) => {
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    grower: id ?? "",
                                }),
                            });
                        }}
                        growerOptions={growerOptions}
                    />
                    <Button
                        size="sm"
                        color={created_date ? "primary" : "default"}
                        onPress={() => {
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    created_date: created_date
                                        ? undefined
                                        : dayjs().format("YYYY-MM-DD"),
                                }),
                            });
                        }}
                        startContent={
                            created_date ? (
                                <FontAwesomeIcon icon={faCheck} />
                            ) : null
                        }
                    >
                        Recently Created
                    </Button>
                </div>
                <Dropdown>
                    <DropdownTrigger>
                        <Button
                            color="primary"
                            size="sm"
                            isDisabled={selectedFields.length === 0}
                            startContent={<FontAwesomeIcon icon={faBars} />}
                        >
                            Actions
                        </Button>
                    </DropdownTrigger>
                    <DropdownMenu>
                        <DropdownItem
                            key="set-crop-planting-date"
                            onPress={() => setShowPlantingForm(true)}
                            startContent={
                                <FontAwesomeIcon
                                    icon={faLeaf}
                                    className="text-primary-500"
                                />
                            }
                        >
                            Set Crop/Planting Date
                        </DropdownItem>
                        <DropdownItem
                            key="create-plot-rx"
                            onPress={() => setShowPlotRxForms(true)}
                            startContent={
                                <FontAwesomeIcon
                                    icon={faDrawPolygon}
                                    className="text-primary-500"
                                />
                            }
                        >
                            Create Plot Rx
                        </DropdownItem>
                        <DropdownItem
                            key="export-plot-rx"
                            onPress={() => setShowBulkExport(true)}
                            startContent={
                                <FontAwesomeIcon
                                    icon={faFileZipper}
                                    className="text-primary-500"
                                />
                            }
                        >
                            Export Plot Rx
                        </DropdownItem>
                        <DropdownItem
                            key="log-plots-application"
                            onPress={() => setShowLogApplicationForm(true)}
                            startContent={
                                <FontAwesomeIcon
                                    icon={faClipboard}
                                    className="text-primary-500"
                                />
                            }
                        >
                            Log Plots Application
                        </DropdownItem>
                        <DropdownItem
                            key="confirm-plots-location"
                            onPress={() => setShowPlotConfirmation(true)}
                            startContent={
                                <FontAwesomeIcon
                                    icon={faCheckDouble}
                                    className="text-primary-500"
                                />
                            }
                        >
                            Confirm Plots Location
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </div>
            <Divider />
            <TableV2
                meta={meta}
                data={tableData}
                columns={columns}
                sorting={sorting}
                setSorting={setSorting}
                manualSorting={true}
                isLoading={isLoading}
                onSelectionChange={handleSelectionChange}
                hasNextPage={fieldsQuery.hasNextPage}
                fetchNextPage={fieldsQuery.fetchNextPage}
                isFetchingNextPage={fieldsQuery.isFetchingNextPage}
            />
            {showPlantingForm && (
                <BulkPlantingOperationForm
                    fields={selectedFields}
                    onFinish={() => {
                        setShowPlantingForm(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {showPlotRxForms && (
                <BulkPlotRxForm
                    fields={selectedFields}
                    onFinish={() => {
                        setShowPlotRxForms(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {showBulkExport && (
                <BulkRxExport
                    prescriptionsToExport={selectedFields
                        .filter(
                            (field) =>
                                field.latest_plot_prescription_id !== null,
                        )
                        .map((field) => {
                            return {
                                fieldId: field.id,
                                prescriptionId:
                                    field.latest_plot_prescription_id as number,
                                fieldName: field.name,
                            };
                        })}
                    onFinish={() => {
                        setShowBulkExport(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {showLogApplicationForm && (
                <BulkLogRxApplied
                    selectedFields={selectedFields}
                    onFinish={() => {
                        setShowLogApplicationForm(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
            {showPlotConfirmation && (
                <BulkConfirmPlots
                    selectedFields={selectedFields}
                    onFinish={() => {
                        setShowPlotConfirmation(false);
                        fieldsQuery.refetch();
                    }}
                />
            )}
        </div>
    );
}
